// R-162 — Terminal screen when the operator's feed lock runs out of docs.
// Clears any residual lock on mount (server is passive on no_candidates per
// design §4.2 — the client owns the terminal). Two ways out: pick a new
// feed or back to dashboard.

import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useFeedLock } from "../hooks/useFeedLock";
import { useOperatorActivity } from "../hooks/useOperatorActivity";
import { MiniSparkline } from "../components/MiniSparkline";
import { useMyActivity } from "@/features/dashboard/hooks/useMyActivity";
import { useAuth } from "@/hooks/useAuth";
import styles from "./FeedExhaustedPage.module.css";

export function FeedExhaustedPage() {
  const { lock, stop } = useFeedLock();
  const { activity } = useMyActivity();
  const { user } = useAuth();
  const { avgTimeSeries, accuracySeries } = useOperatorActivity(user?.userId);
  const [snapshot, setSnapshot] = useState<{ stepType: string } | null>(null);

  // Snapshot the lock at mount BEFORE clearing it — we need the step label
  // for the headline.
  useEffect(() => {
    if (lock && !snapshot) {
      setSnapshot({ stepType: lock.stepType });
    }
  }, [lock, snapshot]);

  useEffect(() => {
    void stop();
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1 className={styles.title}>
          {snapshot
            ? `You've cleared the ${snapshot.stepType} queue.`
            : "Queue is clear."}
        </h1>
        <p className={styles.subtitle}>
          {activity?.docsToday
            ? `${activity.docsToday} document${activity.docsToday === 1 ? "" : "s"} keyed today.`
            : "Nothing more to key right now."}
        </p>
        <div className={styles.actions}>
          <Link to="/queues" className={styles.btnPrimary}>
            Pick another feed
          </Link>
          {/* F-091 — the old secondary read "Back to dashboard" but the natural
              "back" from a feed is /queues (the primary already goes there). Keep
              the overall-dashboard escape hatch, but demote it to a quiet text
              link and name the destination explicitly so it can't be mistaken
              for "back to where I was". */}
          <Link to="/" className={styles.linkQuiet}>
            Overall dashboard
          </Link>
        </div>

        <div className={styles.debrief}>
          <div className={styles.trendCard} data-accent="accent">
            <span className={styles.trendLabel}>
              Time per field (last 7 days)
            </span>
            <MiniSparkline
              data={avgTimeSeries}
              ariaLabel="Average time per field, last 7 days"
            />
          </div>
          <div className={styles.trendCard} data-accent="success">
            <span className={styles.trendLabel}>
              Accuracy trend (last 7 days)
            </span>
            <MiniSparkline
              data={accuracySeries}
              ariaLabel="Accuracy trend, last 7 days"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
